// Frontend authentication configuration for OAuth/OIDC providers
import { type Configuration, LogLevel } from '@azure/msal-browser'
import { AuthProvider } from '@archer/shared'

/**
 * Runtime configuration interface
 * This is populated by config.js which is generated at container startup
 */
interface RuntimeConfig {
  APP_FQDN?: string;
  APP_BASE_URL?: string;
  FRONTEND_URL?: string;
  API_BASE_URL?: string;
  AZURE_AD_ENABLED?: boolean;
  AZURE_AD_CLIENT_ID?: string;
  AZURE_AD_TENANT_ID?: string;
  AZURE_AD_AUTHORITY?: string;
  AZURE_AD_REDIRECT_URI?: string;
  AZURE_AD_POST_LOGOUT_REDIRECT_URI?: string;
  AZURE_AD_SCOPES?: string;
  AZURE_B2C_ENABLED?: boolean;
  AZURE_B2C_CLIENT_ID?: string;
  AZURE_B2C_TENANT_ID?: string;
  AZURE_B2C_AUTHORITY?: string;
  AZURE_B2C_REDIRECT_URI?: string;
  ENABLE_GOOGLE_AUTH?: boolean;
  ENABLE_GITHUB_AUTH?: boolean;
}

// Declare the global runtime config
declare global {
  interface Window {
    runtimeConfig?: RuntimeConfig;
  }
}

/**
 * Get runtime config value with Vite env fallback
 * Priority: runtime config > Vite env > default
 */
function getRuntimeConfig<T>(
  runtimeKey: keyof RuntimeConfig,
  viteKey: string,
  defaultValue: T
): T {
  // First check runtime config (set by Docker entrypoint)
  const runtimeValue = typeof window !== 'undefined' && window.runtimeConfig
    ? window.runtimeConfig[runtimeKey]
    : undefined;

  if (runtimeValue !== undefined && runtimeValue !== null && runtimeValue !== '') {
    return runtimeValue as unknown as T;
  }

  // Then check Vite env vars (set at build time)
  const viteValue = (import.meta as any).env[viteKey];
  if (viteValue !== undefined && viteValue !== null && viteValue !== '') {
    return viteValue as T;
  }

  // Finally return default
  return defaultValue;
}

/**
 * Get the current app base URL, detecting from window.location if not configured
 */
function getAppBaseUrl(): string {
  // Check runtime config first
  if (typeof window !== 'undefined' && window.runtimeConfig?.APP_BASE_URL) {
    return window.runtimeConfig.APP_BASE_URL;
  }

  // Check Vite env
  const viteUrl = (import.meta as any).env.VITE_FRONTEND_URL;
  if (viteUrl) {
    return viteUrl;
  }

  // Default to current origin
  if (typeof window !== 'undefined') {
    return window.location.origin;
  }

  return 'http://localhost:3000';
}

function getApiBaseUrl(): string {
  if (typeof window !== 'undefined' && window.runtimeConfig && 'API_BASE_URL' in window.runtimeConfig) {
    return window.runtimeConfig.API_BASE_URL || '';
  }

  const viteUrl = (import.meta as any).env.VITE_API_BASE_URL;
  if (viteUrl !== undefined && viteUrl !== null) {
    return viteUrl;
  }

  return '';
}

/**
 * Environment variables for frontend auth configuration
 * Uses runtime config with fallback to Vite env vars
 */
export const AUTH_CONFIG = {
  // Azure AD - prefer runtime config, fallback to Vite env
  AZURE_AD_CLIENT_ID: getRuntimeConfig('AZURE_AD_CLIENT_ID', 'VITE_AZURE_AD_CLIENT_ID', ''),
  AZURE_AD_TENANT_ID: getRuntimeConfig('AZURE_AD_TENANT_ID', 'VITE_AZURE_AD_TENANT_ID', ''),
  AZURE_AD_AUTHORITY: getRuntimeConfig('AZURE_AD_AUTHORITY', 'VITE_AZURE_AD_AUTHORITY', ''),
  AZURE_AD_REDIRECT_URI: getRuntimeConfig('AZURE_AD_REDIRECT_URI', 'VITE_AZURE_AD_REDIRECT_URI', ''),
  AZURE_AD_POST_LOGOUT_REDIRECT_URI: getRuntimeConfig('AZURE_AD_POST_LOGOUT_REDIRECT_URI', 'VITE_AZURE_AD_POST_LOGOUT_REDIRECT_URI', ''),
  AZURE_AD_SCOPES: getRuntimeConfig('AZURE_AD_SCOPES', 'VITE_AZURE_AD_SCOPES', 'openid,profile,email,User.Read'),
  AZURE_AD_ENABLED: getRuntimeConfig('AZURE_AD_ENABLED', 'VITE_AZURE_AD_ENABLED', false as boolean) === true ||
    (import.meta as any).env.VITE_AZURE_AD_ENABLED === 'true',

  // Azure AD B2C
  AZURE_B2C_CLIENT_ID: getRuntimeConfig('AZURE_B2C_CLIENT_ID', 'VITE_AZURE_B2C_CLIENT_ID', ''),
  AZURE_B2C_TENANT_ID: getRuntimeConfig('AZURE_B2C_TENANT_ID', 'VITE_AZURE_B2C_TENANT_ID', ''),
  AZURE_B2C_AUTHORITY: getRuntimeConfig('AZURE_B2C_AUTHORITY', 'VITE_AZURE_B2C_AUTHORITY', ''),
  AZURE_B2C_POLICY: (import.meta as any).env.VITE_AZURE_B2C_POLICY || '',
  AZURE_B2C_REDIRECT_URI: getRuntimeConfig('AZURE_B2C_REDIRECT_URI', 'VITE_AZURE_B2C_REDIRECT_URI', ''),
  AZURE_B2C_SCOPES: (import.meta as any).env.VITE_AZURE_B2C_SCOPES || 'openid,profile,email',
  AZURE_B2C_ENABLED: getRuntimeConfig('AZURE_B2C_ENABLED', 'VITE_AZURE_B2C_ENABLED', false as boolean) === true ||
    (import.meta as any).env.VITE_AZURE_B2C_ENABLED === 'true',

  // Google
  GOOGLE_CLIENT_ID: (import.meta as any).env.VITE_GOOGLE_CLIENT_ID || '',
  GOOGLE_REDIRECT_URI: (import.meta as any).env.VITE_GOOGLE_REDIRECT_URI || '',
  GOOGLE_ENABLED: getRuntimeConfig('ENABLE_GOOGLE_AUTH', 'VITE_GOOGLE_ENABLED', false as boolean) === true ||
    (import.meta as any).env.VITE_GOOGLE_ENABLED === 'true',

  // GitHub
  GITHUB_CLIENT_ID: (import.meta as any).env.VITE_GITHUB_CLIENT_ID || '',
  GITHUB_REDIRECT_URI: (import.meta as any).env.VITE_GITHUB_REDIRECT_URI || '',
  GITHUB_ENABLED: getRuntimeConfig('ENABLE_GITHUB_AUTH', 'VITE_GITHUB_ENABLED', false as boolean) === true ||
    (import.meta as any).env.VITE_GITHUB_ENABLED === 'true',

  // General - prefer runtime config, fallback to Vite, then window.location.origin
  API_BASE_URL: getApiBaseUrl(),
  FRONTEND_URL: getAppBaseUrl(),
} as const

function parseScopeList(scopeConfig: string | undefined, fallback: string[]): string[] {
  if (!scopeConfig) return fallback

  const parsed = scopeConfig
    .split(/[\s,]+/)
    .map((scope) => scope.trim())
    .filter(Boolean)

  return parsed.length > 0 ? parsed : fallback
}

/**
 * MSAL Configuration for Azure AD
 */
export function createMSALConfig(): Configuration | null {
  const clientId = AUTH_CONFIG.AZURE_AD_CLIENT_ID
  const tenantId = AUTH_CONFIG.AZURE_AD_TENANT_ID
  
  if (!AUTH_CONFIG.AZURE_AD_ENABLED || !clientId || !tenantId) {
    return null
  }
  
  const authority = AUTH_CONFIG.AZURE_AD_AUTHORITY || 
    `https://login.microsoftonline.com/${tenantId}`
  
  return {
    auth: {
      clientId,
      authority,
      redirectUri: AUTH_CONFIG.AZURE_AD_REDIRECT_URI || 
        `${AUTH_CONFIG.FRONTEND_URL}/auth/callback/azure-ad`,
      postLogoutRedirectUri: AUTH_CONFIG.AZURE_AD_POST_LOGOUT_REDIRECT_URI ||
        `${AUTH_CONFIG.FRONTEND_URL}/auth/logout`,
      navigateToLoginRequestUrl: false,
    },
    cache: {
      cacheLocation: 'sessionStorage', // Use sessionStorage for better security
      storeAuthStateInCookie: false, // Set to true for IE11 or Edge
    },
    system: {
      loggerOptions: {
        loggerCallback: (level, message, containsPii) => {
          if (containsPii) return
          
          switch (level) {
            case LogLevel.Error:
              console.error(message)
              break
            case LogLevel.Info:
              console.info(message)
              break
            case LogLevel.Verbose:
              console.debug(message)
              break
            case LogLevel.Warning:
              console.warn(message)
              break
            default:
              console.log(message)
          }
        },
        logLevel: LogLevel.Warning,
      },
    },
  }
}

/**
 * MSAL Configuration for Azure AD B2C
 */
export function createMSALB2CConfig(): Configuration | null {
  const clientId = AUTH_CONFIG.AZURE_B2C_CLIENT_ID
  const tenantId = AUTH_CONFIG.AZURE_B2C_TENANT_ID
  const policy = AUTH_CONFIG.AZURE_B2C_POLICY
  
  if (!AUTH_CONFIG.AZURE_B2C_ENABLED || !clientId || !tenantId || !policy) {
    return null
  }
  
  const authority = AUTH_CONFIG.AZURE_B2C_AUTHORITY || 
    `https://${tenantId}.b2clogin.com/${tenantId}.onmicrosoft.com/${policy}`
  
  return {
    auth: {
      clientId,
      authority,
      redirectUri: AUTH_CONFIG.AZURE_B2C_REDIRECT_URI || 
        `${AUTH_CONFIG.FRONTEND_URL}/auth/callback/azure-b2c`,
      postLogoutRedirectUri: AUTH_CONFIG.AZURE_AD_POST_LOGOUT_REDIRECT_URI ||
        `${AUTH_CONFIG.FRONTEND_URL}/auth/logout`,
      knownAuthorities: [`${tenantId}.b2clogin.com`],
      navigateToLoginRequestUrl: false,
    },
    cache: {
      cacheLocation: 'sessionStorage',
      storeAuthStateInCookie: false,
    },
    system: {
      loggerOptions: {
        loggerCallback: (level, message, containsPii) => {
          if (containsPii) return
          
          switch (level) {
            case LogLevel.Error:
              console.error(message)
              break
            case LogLevel.Info:
              console.info(message)
              break
            case LogLevel.Verbose:
              console.debug(message)
              break
            case LogLevel.Warning:
              console.warn(message)
              break
            default:
              console.log(message)
          }
        },
        logLevel: LogLevel.Warning,
      },
    },
  }
}

/**
 * Default scopes for Azure AD authentication
 */
export const AZURE_AD_SCOPES = parseScopeList(AUTH_CONFIG.AZURE_AD_SCOPES, [
  'openid',
  'profile', 
  'email',
  'User.Read'
])

export const AZURE_AD_SCOPE_STRING = AZURE_AD_SCOPES.join(' ')

/**
 * Default scopes for Azure AD B2C authentication
 */
export const AZURE_B2C_SCOPES = parseScopeList(AUTH_CONFIG.AZURE_B2C_SCOPES, [
  'openid',
  'profile',
  'email'
])

/**
 * Google OAuth configuration
 */
export const GOOGLE_CONFIG = {
  clientId: AUTH_CONFIG.GOOGLE_CLIENT_ID,
  redirectUri: AUTH_CONFIG.GOOGLE_REDIRECT_URI || 
    `${AUTH_CONFIG.FRONTEND_URL}/auth/callback/google`,
  scope: 'openid profile email',
  responseType: 'code',
  enabled: AUTH_CONFIG.GOOGLE_ENABLED,
}

/**
 * GitHub OAuth configuration
 */
export const GITHUB_CONFIG = {
  clientId: AUTH_CONFIG.GITHUB_CLIENT_ID,
  redirectUri: AUTH_CONFIG.GITHUB_REDIRECT_URI || 
    `${AUTH_CONFIG.FRONTEND_URL}/auth/callback/github`,
  scope: 'user:email read:user',
  enabled: AUTH_CONFIG.GITHUB_ENABLED,
}

/**
 * Get enabled authentication providers
 */
export function getEnabledProviders(): AuthProvider[] {
  const providers: AuthProvider[] = []
  
  if (AUTH_CONFIG.AZURE_AD_ENABLED && AUTH_CONFIG.AZURE_AD_CLIENT_ID) {
    providers.push(AuthProvider.AZURE_AD)
  }
  
  if (AUTH_CONFIG.AZURE_B2C_ENABLED && AUTH_CONFIG.AZURE_B2C_CLIENT_ID) {
    providers.push(AuthProvider.AZURE_AD_B2C)
  }
  
  if (AUTH_CONFIG.GOOGLE_ENABLED && AUTH_CONFIG.GOOGLE_CLIENT_ID) {
    providers.push(AuthProvider.GOOGLE)
  }
  
  if (AUTH_CONFIG.GITHUB_ENABLED && AUTH_CONFIG.GITHUB_CLIENT_ID) {
    providers.push(AuthProvider.GITHUB)
  }
  
  return providers
}

/**
 * Get provider display configuration
 */
export function getProviderDisplayConfig(provider: AuthProvider) {
  const configs = {
    [AuthProvider.AZURE_AD]: {
      name: 'Microsoft Azure AD',
      icon: '/icons/microsoft.svg',
      color: '#0078d4',
      description: 'Sign in with your Microsoft account'
    },
    [AuthProvider.AZURE_AD_B2C]: {
      name: 'Microsoft Azure AD B2C',
      icon: '/icons/microsoft.svg',
      color: '#0078d4',
      description: 'Sign in with Azure AD B2C'
    },
    [AuthProvider.GOOGLE]: {
      name: 'Google',
      icon: '/icons/google.svg',
      color: '#4285f4',
      description: 'Sign in with your Google account'
    },
    [AuthProvider.GITHUB]: {
      name: 'GitHub',
      icon: '/icons/github.svg',
      color: '#333',
      description: 'Sign in with your GitHub account'
    },
    [AuthProvider.OKTA]: {
      name: 'Okta',
      icon: '/icons/okta.svg',
      color: '#007dc1',
      description: 'Sign in with Okta'
    },
    [AuthProvider.AUTH0]: {
      name: 'Auth0',
      icon: '/icons/auth0.svg',
      color: '#eb5424',
      description: 'Sign in with Auth0'
    },
    [AuthProvider.CUSTOM_OIDC]: {
      name: 'Single Sign-On',
      icon: '/icons/sso.svg',
      color: '#6c757d',
      description: 'Sign in with SSO'
    },
  }
  
  return configs[provider] || {
    name: provider,
    icon: '/icons/default.svg',
    color: '#6c757d',
    description: `Sign in with ${provider}`
  }
}
